export class KV {

    static create(options) { return new this(options); }

    #path;
    #ttl;
    #registry;
    #origins;
    #options;
    #fireHook;
    #serializeHook;
    #deserializeHook;

    get path() { return this.#path; }
    get ttl() { return this.#ttl; }
    get hasTTL() { return this.#ttl !== null; }
    get registry() { return this.#registry; }
    get origins() { return this.#origins; }
    get options() { return this.#options; }
    get fieldLevelExpiry() { return true; }

    constructor({ path, ttl = null, registry = new Map, origins = [], fireHook = null, serializeHook = null, deserializeHook = null, ...options } = {}) {
        if (!Array.isArray(path)) {
            throw new Error('Path must be an array if provided');
        }
        this.#path = path;
        if (ttl !== null && typeof ttl !== 'number') {
            throw new Error('TTL must be a number (in milliseconds) if provided; null otherwise');
        }
        this.#ttl = ttl;
        if (!(registry instanceof Map)) {
            throw new Error('Registry must be an instance of Map if provided');
        }
        this.#registry = registry;
        if (!Array.isArray(origins)) {
            throw new Error('Origins must be an array if provided');
        }
        this.#origins = origins;
        this.#options = options;
        if (fireHook && typeof fireHook !== 'function') {
            throw new Error('fireHook must be a function if provided');
        }
        this.#fireHook = fireHook;
        if (serializeHook && typeof serializeHook !== 'function') {
            throw new Error('serializeHook must be a function if provided');
        }
        this.#serializeHook = serializeHook || ((val, ...args) => (val === undefined ? null : JSON.stringify(val, ...args)));
        if (deserializeHook && typeof deserializeHook !== 'function') {
            throw new Error('deserializeHook must be a function if provided');
        }
        this.#deserializeHook = deserializeHook || ((val) => (val === null ? undefined : JSON.parse(val)));
    }

    _serialize(val, ...args) { return this.#serializeHook(val, ...args); }
    _deserialize(val) { return this.#deserializeHook(val); }

    _path(path, create = true) {
        if (!Array.isArray(path)) {
            throw new Error(`Path length cannot be 0`);
        }
        /*
        base -> {
            subtree: uuid -> {
                    subtree: field -> {
                            subtree,
                            entries: Set<fn>
                        },
                    entries: Set<fn>
                },
            entries: Set<fn>
        }
        */
        return path.reduce((node, key, i) => {
            if (create && !node?.subtree.has(key)) {
                const subtree = new Map;
                const entries = new Set;
                const dispose = () => {
                    node.subtree.delete(key);
                    if (!node.subtree.size && !node.entries?.size) {
                        //node.dispose?.();
                    }
                };
                node.subtree.set(key, { subtree, entries, context: node, dispose });
            }
            if (create === 0 && !node?.subtree.has(key)) {
                return node;
            }
            return node?.subtree.get(key);
        }, { subtree: this.#registry, entries: new Set });
    }

    _subscribe(path, callback, options = {}) {
        const node = this._path(path, true);

        const dispose = () => {
            node.entries.delete(subscription);
            if (!node.entries.size) {
                node.dispose();
            }
        };

        const subscription = { callback, options, origins: this.#origins, dispose };
        node.entries.add(subscription);

        if (options.signal) {
            options.signal.addEventListener('abort', dispose);
        }

        return dispose;
    }

    async _fire({ path = this.#path, origins = this.#origins, timestamp = Date.now(), ...event }) {
        if (!['set', 'delete', 'clear', 'patch'].includes(event.type)) {
            throw new Error(`Invalid event`);
        }
        const node = this._path(event.key ? path.concat(event.key) : path, 0);
        if (!node) return;

        const entries = [];
        let _node = node;
        do {
            entries.push(..._node.entries);
        } while ((_node = _node.context) && _node.entries);

        const returnValues = [];
        const fire = (subscription, _event = event) => {
            const { callback, options, origins: subscriptionOrigins, dispose } = subscription;

            let i = origins.length - 1;
            for (; i >= (options.scope || 0); i--) {
                if (subscriptionOrigins[i] !== origins[i]) return;
            }

            returnValues.push(callback({ ..._event, path, scope: i + 1, origins, timestamp }));

            if (options.once) dispose();
        };

        entries.forEach((subscription) => fire(subscription));

        if (event.type === 'clear' || event.type === 'patch') {
            const node = this._path(path, false);
            for (const [key, _node] of node?.subtree.entries() || []) {

                let _event = { type: 'delete', key, detail: event.detail };
                if (event.type === 'patch' && event.data && typeof event.data === 'object') {
                    if (key in event.data) {
                        _event = { type: 'set', key, value: event.data[key], detail: event.detail };
                    } else if (!event.replace) {
                        continue;
                    }
                }

                for (const subscription of _node.entries) {
                    fire(subscription, _event);
                }
            }
        }

        if (this.#fireHook) {
            returnValues.push(this.#fireHook({ path, origins, timestamp, ...event }));
        }

        await Promise.all(returnValues);
    }

    _normalizeExpires(expires) {
        if (!(expires ?? false)) return;

        // Date instance
        if (expires instanceof Date) {
            return expires.getTime();
        }

        // Numeric timestamp (seconds or ms)
        if (typeof expires === 'number') {
            // Heuristic: seconds are too small to be ms
            return expires < 1e12
                ? expires * 1000
                : expires;
        }

        // ISO date string
        if (typeof expires === 'string') {
            const ts = Date.parse(expires);
            if (!Number.isNaN(ts)) return ts;
        }

        throw new TypeError(`Invalid expires value: ${expires}`);
    }

    _expired(node) {
        if (!this.hasTTL || !this.fieldLevelExpiry) return false;
        return !!(node?.expires && node.expires <= Date.now());
    }

    _resolveSet(key, value, options) {
        const isSelector = typeof key === 'object' && key;
        let rest;
        ({ key, value, ...rest } = isSelector ? key : { key, value });
        if (this.hasTTL && this.fieldLevelExpiry) {
            if (!rest.expires) {
                // Auto-derived from top-level TTL
                rest.expires = Date.now() + this.ttl;
            } else {
                // Normalize expires
                rest.expires = this._normalizeExpires(rest.expires);
            }
        }

        const event = {
            type: 'set',
            key,
            value,
            path: this.path,
            detail: options?.detail,
            origins: this.origins,
            timestamp: Date.now(),
        };

        return { key, value, rest, event };
    }

    _resolveInputPatch(obj, options) {
        if (typeof obj !== 'object') {
            throw new Error(`Argument must be a valid JSON object`);
        }

        const expires = this.hasTTL && this.fieldLevelExpiry ? Date.now() + this.ttl : null;
        const plainData = {};
        const data = {};
        for (const [key, value] of Object.entries(obj)) {
            if (options.meta && !(value && typeof value === 'object')) {
                throw new Error(`A hash expected for field ${key}`);
            }
            plainData[key] = options.meta ? value.value : value;
            data[key] = options.meta ? value : { value };
            if (expires && !data[key].expires) {
                // Auto-derived from top-level TTL
                data[key].expires = expires;
            } else if (this.hasTTL && this.fieldLevelExpiry && data[key].expires) {
                // Normalize expires
                data[key].expires = this._normalizeExpires(data[key].expires);
            }
        }

        const event = {
            type: 'patch',
            data: plainData,
            meta: !!options?.meta,
            replace: !!options?.replace,
            detail: options?.detail,
            path: this.path,
            origins: this.origins,
            timestamp: Date.now(),
        };

        return { data, event };
    }

    _resolveDelete(key, options) {
        key = typeof key === 'object' && key ? key.key : key;

        const event = {
            type: 'delete',
            key,
            path: this.path,
            detail: options?.detail,
            origins: this.origins,
            timestamp: Date.now(),
        };

        return { key, event };
    }

    _resolveClear(options) {
        const event = {
            type: 'clear',
            path: this.path,
            detail: options?.detail,
            origins: this.origins,
            timestamp: Date.now(),
        };

        return { event };
    }

    // ----------

    subscribe(key, callback, options = {}) {
        if (typeof key === 'function') {
            options = callback || {};
            callback = key;
            key = [];
        }
        const fieldPath = this.#path.concat(key);
        return this._subscribe(fieldPath, callback, options);
    }

    cleanup() { this._path(this.#path, false)?.dispose(); }

    async close() { }
}